/**
 * Набор утилит общего назначения
 * @namespace js:utils/common
 */

export default {

    /**
     * Возвращает url текущей страницы
     *
     * @memberOf js:utils
     * @returns {string}
     */
    getCurrentLocation() {
        return window.location.href;
    },

    /**
     * Проверка значения. Если оно равно null, undefined, false или пустая строка, то возвращаем значение по
     * умолчанию defaultValue. В противном случае возвращаем само значение
     *
     * @memberOf js:utils
     * @param {any} value - Проверяемое значение
     * @param {any} defaultValue - Значение по умолчанию
     * @returns {any}
     */
    checkValue(value, defaultValue) {
        if (_.isBoolean(value)) {
            return value;
        } else {
            defaultValue = defaultValue ? defaultValue : '';
            return value ? value : defaultValue;
        }
    },

    /**
     * Обработка ответа ajax-запроса
     *
     * @memberOf js:utils
     * @param {object} error - Данные ajax-запроса (см. в сторону axios)
     * @returns {string}
     */
    getResponseError(error) {
        let message = '';
        if (_.isObject(error)) {
            let data = error.response.data;
            message = data.message;
            if (data.data !== undefined && !_.isEmpty(data.data.err_messages) && _.isObject(data.data.err_messages)) {
                message = _.values(data.data.err_messages).join(' ');
            }
        } else {
            message = error;
        }
        return message;
    },

    /**
     * Метод меняющий адресную строку веб-браузера без полной перезагрузки. Если такое невозможно, то выполняется
     * переход по текущему адресу + тег #{curLoc}. Если задан параметр timeout, то переход будет осуществлен по истечению
     * этого времени
     * @param curLoc
     * @param timeout
     */
    setLocation(curLoc, timeout){
        try {
            history.pushState(null, null, curLoc);
            return;
        } catch(e) {}
        this.redirect(curLoc, '#' + curLoc);
    },

    /**
     * Переходит по указанному адресу. Если указан параметр timeout, то переход будет осуществлен по истечению
     * этого времени
     *
     * @param curLoc
     * @param timeout
     */
    redirect(curLoc, timeout){
        timeout = timeout || 0;
        setTimeout(function(){
            if(curLoc.indexOf(0) === '#'){
                window.location.hash = curLoc;
            }
            else{
                window.location = curLoc;
            }
        }, timeout);
    },

    /**
     * Возвращаем из списка элемент, чей id равен указанному во входящих параметрах
     *
     * @memberOf js:utils
     * @param {array} list
     * @param {numeric} id
     * @returns {undefined | object}
     */
    getItemById(list, id) {
        let result = undefined;
        if (list && _.isArray(list)) {
            result = _.find(list, {'id': id});
        }
        return result;
    },

    /**
     * Возвращаем индекс элемента, чей id равен указанному во входящих параметрах
     *
     * @memberOf js:utils
     * @param {array} list
     * @param {numeric} id
     * @returns {numeric} - Индекс элемента
     */
    getIndexById(list, id) {
        return _.findIndex(list, {id: id});
    },

    htmlId(prefix, id) {
        return prefix + '_' + id
    },

    /**
     * Метод для обработки HTTP запросов
     *
     * @memberOf js:utils
     * @param {string} type - Тип HTTP запроса (get, put, post, delete)
     * @param {string} url - URL HTTP запроса
     * @param {object} params - Параметры запроса
     * @param {function} callback - Callback-функция при успешном выполнении HTTP-запроса
     * @param {function} callbackError - Callback-функция при ошибке HTTP-запроса
     */
    requestHttp(type, url, params, callback, callbackError) {
        params = this.checkValue(params, {});

        switch(type) {
            case 'post':
                this.requestPost(url, params, callback, callbackError);
                break;

            case 'put':
                this.requestPut(url, params, callback, callbackError);
                break;

            case 'delete':
                this.requestDelete(url, params, callback, callbackError);
                break;

            default:
                this.requestGet(url, params, callback, callbackError);
                break;
        }
    },

    /**
     * Метод для обработки get запроса
     *
     * @memberOf js:utils
     * @param {string} url - URL HTTP запроса
     * @param {object} params - Параметры запроса
     * @param {function} callback - Callback-функция при успешном выполнении HTTP-запроса
     * @param {function} callbackError - Callback-функция при ошибке HTTP-запроса
     */
    requestGet(url, params, callback, callbackError) {
        params = this.checkValue(params, {});
        params.timestamp = Date.now();
        axios.get(url, { params: params})
            .then((response) => {
                if (_.isFunction(callback)) callback(response);
            }).catch(error => {
            if (_.isFunction(callbackError)) callbackError(error);
        });
    },

    /**
     * Метод для обработки post запроса
     *
     * @memberOf js:utils
     * @param {string} url - URL HTTP запроса
     * @param {object} params - Параметры запроса
     * @param {function} callback - Callback-функция при успешном выполнении HTTP-запроса
     * @param {function} callbackError - Callback-функция при ошибке HTTP-запроса
     */
    requestPost(url, params, callback, callbackError) {
        axios.post(url, params)
            .then((response) => {
                if (_.isFunction(callback)) callback(response);
            }).catch(error => {
            if (_.isFunction(callbackError)) callbackError(error);
        });
    },

    /**
     * Метод для обработки put запроса
     *
     * @memberOf js:utils
     * @param {string} url - URL HTTP запроса
     * @param {object} params - Параметры запроса
     * @param {function} callback - Callback-функция при успешном выполнении HTTP-запроса
     * @param {function} callbackError - Callback-функция при ошибке HTTP-запроса
     */
    requestPut(url, params, callback, callbackError) {
        axios.put(url, params)
            .then((response) => {
                if (_.isFunction(callback)) callback(response);
            }).catch(error => {
            if (_.isFunction(callbackError)) callbackError(error);
        });
    },

    /**
     * Метод для обработки delete запроса
     *
     * @memberOf js:utils
     * @param {string} url - URL HTTP запроса
     * @param {object} params - Параметры запроса
     * @param {function} callback - Callback-функция при успешном выполнении HTTP-запроса
     * @param {function} callbackError - Callback-функция при ошибке HTTP-запроса
     */
    requestDelete(url, params, callback, callbackError) {
        axios.delete(url, { data: params })
            .then((response) => {
                if (_.isFunction(callback)) callback(response);
            }).catch(error => {
            if (_.isFunction(callbackError)) callbackError(error);
        });
    },

    /**
     * Клонируем объект. Почему-то иногда Object.assign не спасает, если пытаемся клонировать объект, который является
     * свойством другого объекта
     *
     * @memberOf js:utils
     * @param {object} obj
     * @returns {obj}
     */
    cloneObject(obj) {
        return  JSON.parse(JSON.stringify(obj));
    },

    formatDate(date) {
    var monthNames = [
        "01", "02", "03",
        "04", "05", "06", "07",
        "08", "09", "10",
        "11", "12"
    ];

    var day = ('0' + date.getDate()).slice(-2);
    var monthIndex = date.getMonth();
    var year = date.getFullYear();

    return day + '.' + monthNames[monthIndex] + '.' + year;
}

}
